import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Cake, Download, RefreshCw, Copy, Check, Users, Baby, CalendarClock, CalendarRange, ImagePlus, X } from "lucide-react";
import { apiFetch } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Voltar dessas telas de administração deve ir pro painel de admin (/admin),
// não pro site.
const ADMIN_PATH = "/admin";

const MONTH_NAMES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
];

// Banco de frases pra sortear na arte -- só front, não depende do backend
// (o backend só precisa dizer QUEM faz aniversário; a frase é só detalhe de
// apresentação da arte/texto que a secretaria compartilha no WhatsApp).
const BIRTHDAY_MESSAGES = [
  "Que Deus continue escrevendo uma história linda na sua vida. Feliz aniversário!",
  "Um novo ano de vida, uma nova chance de viver tudo o que Deus preparou pra você. Parabéns!",
  "Que este novo ciclo venha cheio de saúde, alegria e a presença de Deus em cada detalhe.",
  "A igreja Mosaico é mais bonita com você nela. Que seu dia seja tão especial quanto você!",
  "Que os planos de Deus pra sua vida se cumpram, um aniversário de cada vez. Feliz aniversário!",
  "Comemorar sua vida é uma alegria pra toda a nossa família da fé. Parabéns e muitas bênçãos!",
  "Que a bondade de Deus te acompanhe todos os dias deste novo ano. Feliz aniversário!",
  "Você é um presente pra nossa comunidade. Que Deus te presenteie com um ano incrível!",
  "Parabéns! Que a alegria do Senhor seja a sua força neste novo ciclo.",
  "Feliz aniversário! Que este dia seja repleto do amor de Deus e das pessoas que você ama.",
  "Que cada ano que passa te aproxime ainda mais do propósito que Deus tem pra sua vida.",
  "A Mosaico celebra com você hoje! Que Deus renove suas forças e multiplique suas alegrias.",
  "Que a graça de Deus seja renovada sobre você a cada manhã deste novo ano. Feliz aniversário!",
  "Parabéns pelo seu dia! Que venham muitas risadas, saúde e a paz de Deus em abundância.",
  "Hoje o céu celebra com a gente: mais um ano de vida revelando o cuidado fiel de Deus por você. Feliz aniversário!",
  "Que a sua fé seja inabalável neste novo ano -- não importa a tempestade, a âncora continua firme em Deus. Parabéns!",
  "Você foi pensado(a) antes da fundação do mundo, e hoje celebramos isso de coração cheio. Feliz aniversário!",
  "Que este novo ciclo derrube toda barreira e abra as portas que só Deus pode abrir na sua vida. Parabéns!",
  "A sua vida é um testemunho vivo da graça de Deus. Que este ano seja de vitória sobre vitória!",
  "Celebramos hoje não só mais um ano, mas tudo que Deus já fez e tudo que ainda vai fazer através de você. Parabéns!",
  "Que a verdade de Deus seja a única voz que você segue, guiando cada decisão deste novo ano com coragem.",
  "Deus nunca erra o tempo de ninguém -- e hoje é o seu tempo de celebrar tudo que Ele construiu em você. Feliz aniversário!",
  "Que você viva este novo ano com ousadia de fé e o coração sempre voltado pro que realmente importa.",
  "A Mosaico ora hoje por mais vida, mais saúde e mais da presença de Deus sobre você. Parabéns, nosso presente de Deus!",
  "Que nenhuma palavra torta tenha vez na sua vida -- só a Palavra de Deus, viva e forte, guiando cada passo deste novo ano.",
  "Que a coragem de confiar em Deus seja maior que qualquer medo neste novo ano da sua história. Feliz aniversário!",
  "Feliz aniversário! Que o Senhor abra diante de você portas que nenhum homem pode fechar neste novo ano.",
  "Cada marca no calendário é mais uma prova do cuidado de Deus com a sua história. Celebramos isso hoje com você!",
  "Que você experimente, de um jeito novo, a largura, a altura e a profundidade do amor de Deus por você. Parabéns!",
  "A sua caminhada inspira a nossa família da fé. Que este novo ano seja de muito crescimento e alegria genuína.",
  "Hoje é dia de parar e agradecer por você: sua vida, sua história e tudo que Deus ainda vai escrever nela. Feliz aniversário!",
  "Que a paz de Deus guarde seu coração e sua mente neste novo ciclo, não importa o que vier. Parabéns!",
  "Deus te chamou pelo nome e te conhece profundamente -- e hoje celebramos mais um ano dessa relação linda. Feliz aniversário!",
  "Que você continue sendo luz por onde passar, do jeito que só você sabe ser. Muitas felicidades neste novo ano!",
  "A Mosaico agradece a Deus por mais um ano da sua vida entre nós. Que venham bênçãos em dobro!",
  "Que o favor de Deus te abra caminhos antes mesmo que você peça neste novo ano. Feliz aniversário!",
  "Você é parte da história que Deus está escrevendo aqui na Mosaico -- e hoje celebramos esse capítulo com muita gratidão!",
  "Que a alegria de hoje seja só uma amostra do que Deus reservou pra você neste novo ano. Parabéns!",
  "Feliz aniversário! Que cada passo deste novo ciclo seja guiado pela mão fiel de Deus."
];

function pickRandomMessage(excludeIndex) {
  if (BIRTHDAY_MESSAGES.length <= 1) return { text: BIRTHDAY_MESSAGES[0], index: 0 };
  let index = Math.floor(Math.random() * BIRTHDAY_MESSAGES.length);
  while (index === excludeIndex) index = Math.floor(Math.random() * BIRTHDAY_MESSAGES.length);
  return { text: BIRTHDAY_MESSAGES[index], index };
}

// Catalogo de estilos de fundo -- a secretaria escolhe DELIBERADAMENTE na
// galeria (ver StyleGallery mais abaixo) em vez da arte sortear sozinha, pra
// poder combinar o estilo com quem esta fazendo aniversario (algo ludico pra
// uma crianca, algo mais sobrio pra um membro adulto) em vez da mesma cara
// fria pra todo mundo. 3 grupos: liso (cor solida), degrade (gradiente) e
// padrao (gradiente/solido + um desenho abstrato por cima). O conteudo do
// cartao (nome, frase, painel escuro) NAO muda de cor entre estilos -- so o
// fundo muda -- entao contraste e identidade visual ficam estaveis com
// qualquer escolha.

// Preenche `size`x`size` com uma cor solida (1 cor) ou gradiente linear
// (2-3 cores) -- generaliza o antigo addColorStop(0)/addColorStop(0.45)/
// addColorStop(1) fixo pra qualquer quantidade de cores.
function fillBackground(ctx, size, colors) {
  if (colors.length === 1) {
    ctx.fillStyle = colors[0];
    ctx.fillRect(0, 0, size, size);
    return;
  }
  const gradient = ctx.createLinearGradient(0, 0, size, size);
  colors.forEach((color, i) => {
    gradient.addColorStop(i / (colors.length - 1), color);
  });
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
}

// Gerador pseudo-aleatorio determinístico (mesma seed = mesmo resultado
// sempre) -- os padroes abaixo usam isso em vez de Math.random() pra nao
// "pular" de posicao toda vez que o card redesenha (o card redesenha mais de
// uma vez: ao carregar fontes, ao trocar frase, ao trocar estilo -- com
// Math.random() de verdade o confete/estrelas ficariam piscando em posicoes
// diferentes a cada redesenho, parecendo bug).
function makeSeededRandom(seed) {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
}

// Circulos translucidos suaves, so pra dar textura nos estilos lisos/
// degrade (sem arquivo de imagem externo) -- os estilos com padrao dedicado
// (confete, bolinhas etc.) nao usam isso, pra nao poluir com dois efeitos
// sobrepostos.
function drawSoftGlowCircles(ctx, size) {
  const spots = [
    [0.120, 0.139, 0.083],
    [0.889, 0.130, 0.065],
    [0.102, 0.880, 0.056],
    [0.907, 0.898, 0.102],
    [0.519, 0.074, 0.037]
  ];
  spots.forEach(([fx, fy, fr]) => {
    ctx.beginPath();
    ctx.fillStyle = "rgba(255,255,255,0.10)";
    ctx.arc(fx * size, fy * size, fr * size, 0, Math.PI * 2);
    ctx.fill();
  });
}

function drawPatternConfete(ctx, size) {
  const colors = ["#fbbf24", "#f472b6", "#34d399", "#60a5fa", "#ffffff"];
  const rand = makeSeededRandom(42);
  const count = Math.round(size / 20);
  for (let i = 0; i < count; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const w = size * 0.02;
    const h = size * 0.032;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rand() * Math.PI);
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = colors[i % colors.length];
    ctx.fillRect(-w / 2, -h / 2, w, h);
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

function drawPatternBolinhas(ctx, size) {
  const spacing = size * 0.09;
  const r = size * 0.018;
  ctx.fillStyle = "rgba(255,255,255,0.16)";
  let row = 0;
  for (let y = spacing * 0.5; y < size; y += spacing) {
    const offset = (row % 2) * (spacing / 2);
    for (let x = offset; x < size + spacing; x += spacing) {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    row++;
  }
}

function drawPatternOndas(ctx, size) {
  const bands = [
    { y: size * 0.28, amp: size * 0.035, color: "rgba(255,255,255,0.12)" },
    { y: size * 0.55, amp: size * 0.045, color: "rgba(255,255,255,0.10)" },
    { y: size * 0.82, amp: size * 0.03, color: "rgba(255,255,255,0.14)" }
  ];
  bands.forEach(({ y, amp, color }) => {
    ctx.beginPath();
    ctx.moveTo(0, y);
    const step = size / 8;
    for (let x = 0; x <= size; x += step) {
      ctx.lineTo(x, y + Math.sin((x / size) * Math.PI * 2.5) * amp);
    }
    ctx.lineTo(size, size);
    ctx.lineTo(0, size);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  });
}

function drawPatternListras(ctx, size) {
  const stripeW = size * 0.09;
  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate(-Math.PI / 8);
  ctx.translate(-size / 2, -size / 2);
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  for (let x = -size; x < size * 2; x += stripeW * 2) {
    ctx.fillRect(x, -size * 0.5, stripeW, size * 2);
  }
  ctx.restore();
}

function drawStarShape(ctx, cx, cy, r) {
  const spikes = 4;
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const angle = (Math.PI / spikes) * i;
    const radius = i % 2 === 0 ? r : r * 0.4;
    const x = cx + Math.cos(angle) * radius;
    const y = cy + Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

function drawPatternEstrelas(ctx, size) {
  const rand = makeSeededRandom(7);
  const count = Math.round(size / 28);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  for (let i = 0; i < count; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = size * (0.007 + rand() * 0.011);
    ctx.globalAlpha = 0.35 + rand() * 0.5;
    drawStarShape(ctx, x, y, r);
  }
  ctx.globalAlpha = 1;
}

// Ladrilhos triangulares em cores da marca -- um aceno visual direto ao
// nome/logo da igreja ("Mosaico"), nao so mais um padrao generico.
function drawPatternMosaico(ctx, size) {
  const colors = ["#f3a952", "#3e82ff", "#34d399", "#fb7185", "#fbbf24", "#7c3aed"];
  const cell = size * 0.12;
  const rand = makeSeededRandom(19);
  let colorIdx = 0;
  for (let y = 0; y < size; y += cell) {
    for (let x = 0; x < size; x += cell) {
      ctx.beginPath();
      if (rand() > 0.5) {
        ctx.moveTo(x, y);
        ctx.lineTo(x + cell, y);
        ctx.lineTo(x, y + cell);
      } else {
        ctx.moveTo(x + cell, y);
        ctx.lineTo(x + cell, y + cell);
        ctx.lineTo(x, y + cell);
      }
      ctx.closePath();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = colors[colorIdx % colors.length];
      ctx.fill();
      colorIdx++;
    }
  }
  ctx.globalAlpha = 1;
}

// Baloes espalhados (corpo oval + biquinho + barbante) -- pensado
// especialmente pros aniversariantes Kids/Teens, mais ludico que os
// padroes genericos acima.
function drawPatternBaloes(ctx, size) {
  const colors = ["#ffffff", "#fde68a", "#f472b6", "#60a5fa"];
  const rand = makeSeededRandom(58);
  const count = Math.round(size / 130);
  for (let i = 0; i < count; i++) {
    const x = rand() * size;
    const y = rand() * size * 0.85;
    const r = size * (0.035 + rand() * 0.02);
    ctx.save();
    ctx.globalAlpha = 0.4 + rand() * 0.3;
    ctx.fillStyle = colors[i % colors.length];
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * 1.22, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x - r * 0.14, y + r * 1.14);
    ctx.lineTo(x + r * 0.14, y + r * 1.14);
    ctx.lineTo(x, y + r * 1.32);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = ctx.fillStyle;
    ctx.lineWidth = size * 0.003;
    ctx.beginPath();
    ctx.moveTo(x, y + r * 1.32);
    ctx.lineTo(x, y + r * 1.9);
    ctx.stroke();
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

// Manchas organicas suaves (blobs) desfocadas -- pedido: um estilo "abstrato",
// diferente dos padroes geometricos acima (confete, bolinhas, listras etc.).
// ctx.filter blur da o efeito de tinta se espalhando; cada estilo passa sua
// propria paleta (quente/fria) pra dar 2 variacoes com a mesma tecnica.
function makeDrawPatternAbstrato(colors, seed) {
  return function drawPatternAbstrato(ctx, size) {
    const rand = makeSeededRandom(seed);
    ctx.save();
    ctx.filter = `blur(${Math.round(size * 0.05)}px)`;
    const count = 5;
    for (let i = 0; i < count; i++) {
      const x = rand() * size;
      const y = rand() * size;
      const r = size * (0.16 + rand() * 0.16);
      ctx.globalAlpha = 0.32 + rand() * 0.28;
      ctx.fillStyle = colors[i % colors.length];
      ctx.beginPath();
      ctx.ellipse(x, y, r, r * (0.7 + rand() * 0.6), rand() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  };
}

const drawPatternAbstratoQuente = makeDrawPatternAbstrato(["#fb7185", "#f3a952", "#a78bfa", "#fbbf24"], 93);
const drawPatternAbstratoFrio = makeDrawPatternAbstrato(["#60a5fa", "#34d399", "#818cf8", "#22d3ee"], 61);

// Raios de luz irradiando do centro -- cunhas translucidas alternadas,
// geometrico mas sem a frieza de bolinhas/listras.
function drawPatternRaios(ctx, size) {
  const rayCount = 16;
  const cx = size / 2;
  const cy = size / 2;
  const halfSpread = Math.PI / rayCount;
  ctx.save();
  ctx.translate(cx, cy);
  for (let i = 0; i < rayCount; i++) {
    ctx.save();
    ctx.rotate((Math.PI * 2 * i) / rayCount);
    ctx.globalAlpha = i % 2 === 0 ? 0.16 : 0.07;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, size * 0.75, -halfSpread, halfSpread);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

function drawHeartShape(ctx, cx, cy, r) {
  const top = r * 0.3;
  ctx.beginPath();
  ctx.moveTo(cx, cy + top);
  ctx.bezierCurveTo(cx, cy, cx - r, cy, cx - r, cy + top);
  ctx.bezierCurveTo(cx - r, cy + r * 0.6, cx, cy + r * 0.9, cx, cy + r * 1.3);
  ctx.bezierCurveTo(cx, cy + r * 0.9, cx + r, cy + r * 0.6, cx + r, cy + top);
  ctx.bezierCurveTo(cx + r, cy, cx, cy, cx, cy + top);
  ctx.closePath();
  ctx.fill();
}

// Coracoes pequenos espalhados -- pra mensagens mais afetivas/familiares,
// seguindo o mesmo esquema de seed fixa dos outros padroes (nao "pula").
function drawPatternCoracoes(ctx, size) {
  const colors = ["#fda4af", "#fb7185", "#f472b6", "#ffffff"];
  const rand = makeSeededRandom(77);
  const count = Math.round(size / 55);
  for (let i = 0; i < count; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = size * (0.018 + rand() * 0.016);
    ctx.globalAlpha = 0.3 + rand() * 0.35;
    ctx.fillStyle = colors[i % colors.length];
    drawHeartShape(ctx, x, y, r);
  }
  ctx.globalAlpha = 1;
}

const BACKGROUND_PATTERNS = {
  confete: drawPatternConfete,
  bolinhas: drawPatternBolinhas,
  ondas: drawPatternOndas,
  listras: drawPatternListras,
  estrelas: drawPatternEstrelas,
  mosaico: drawPatternMosaico,
  baloes: drawPatternBaloes,
  "abstrato-quente": drawPatternAbstratoQuente,
  "abstrato-frio": drawPatternAbstratoFrio,
  raios: drawPatternRaios,
  coracoes: drawPatternCoracoes
};

const BACKGROUND_STYLES = [
  { id: "liso-indigo", label: "Índigo", kind: "liso", colors: ["#2b3467"] },
  { id: "liso-vinho", label: "Vinho", kind: "liso", colors: ["#7c2d3c"] },
  { id: "degrade-sunset", label: "Pôr do sol", kind: "degrade", colors: ["#f3a952", "#f08a3c", "#3e82ff"] },
  { id: "degrade-rosa", label: "Rosa & roxo", kind: "degrade", colors: ["#fb7185", "#ec4899", "#6d28d9"] },
  { id: "degrade-verde", label: "Verde & azul", kind: "degrade", colors: ["#34d399", "#0ea5e9", "#4338ca"] },
  { id: "padrao-confete", label: "Confete", kind: "padrao", colors: ["#7c3aed", "#4c1d95"], pattern: "confete" },
  { id: "padrao-bolinhas", label: "Bolinhas", kind: "padrao", colors: ["#0ea5e9", "#0c4a6e"], pattern: "bolinhas" },
  { id: "padrao-ondas", label: "Ondas", kind: "padrao", colors: ["#22d3ee", "#1e40af"], pattern: "ondas" },
  { id: "padrao-listras", label: "Listras", kind: "padrao", colors: ["#fb7185", "#be123c"], pattern: "listras" },
  { id: "padrao-estrelas", label: "Estrelas", kind: "padrao", colors: ["#1e1b4b", "#0f172a"], pattern: "estrelas" },
  { id: "padrao-mosaico", label: "Mosaico", kind: "padrao", colors: ["#f3a952", "#3e82ff"], pattern: "mosaico" },
  { id: "liso-verde-musgo", label: "Verde musgo", kind: "liso", colors: ["#1f4d3a"] },
  { id: "liso-marinho", label: "Marinho", kind: "liso", colors: ["#0f2942"] },
  { id: "degrade-dourado", label: "Dourado", kind: "degrade", colors: ["#fde68a", "#f3a952", "#92400e"] },
  { id: "degrade-lilas", label: "Lilás & azul", kind: "degrade", colors: ["#c4b5fd", "#818cf8", "#1e3a8a"] },
  { id: "padrao-baloes", label: "Balões", kind: "padrao", colors: ["#ec4899", "#7c3aed"], pattern: "baloes" },
  { id: "padrao-confete-verde", label: "Confete verde", kind: "padrao", colors: ["#065f46", "#022c22"], pattern: "confete" },
  { id: "padrao-ondas-dourado", label: "Ondas douradas", kind: "padrao", colors: ["#f3a952", "#7c4a12"], pattern: "ondas" },
  { id: "padrao-abstrato-quente", label: "Abstrato quente", kind: "padrao", colors: ["#1f1147", "#3b0764"], pattern: "abstrato-quente" },
  { id: "padrao-abstrato-frio", label: "Abstrato frio", kind: "padrao", colors: ["#0c1a2e", "#0f2942"], pattern: "abstrato-frio" },
  { id: "liso-grafite", label: "Grafite", kind: "liso", colors: ["#1e293b"] },
  { id: "liso-terracota", label: "Terracota", kind: "liso", colors: ["#9a3412"] },
  { id: "degrade-aurora", label: "Aurora", kind: "degrade", colors: ["#2dd4bf", "#6366f1", "#312e81"] },
  { id: "degrade-vinho-ouro", label: "Vinho & ouro", kind: "degrade", colors: ["#7c2d3c", "#9a3412", "#f3a952"] },
  { id: "padrao-listras-azul", label: "Listras azuis", kind: "padrao", colors: ["#1e3a8a", "#0c1a2e"], pattern: "listras" },
  { id: "padrao-bolinhas-dourado", label: "Bolinhas douradas", kind: "padrao", colors: ["#92400e", "#451a03"], pattern: "bolinhas" },
  { id: "padrao-estrelas-dourado", label: "Estrelas douradas", kind: "padrao", colors: ["#3b2f05", "#1c1506"], pattern: "estrelas" },
  { id: "padrao-mosaico-noturno", label: "Mosaico noturno", kind: "padrao", colors: ["#1e1b4b", "#020617"], pattern: "mosaico" },
  { id: "padrao-raios-quente", label: "Raios de luz", kind: "padrao", colors: ["#f3a952", "#9a3412"], pattern: "raios" },
  { id: "padrao-raios-azul", label: "Raios azuis", kind: "padrao", colors: ["#1d4ed8", "#0f172a"], pattern: "raios" },
  { id: "padrao-coracoes-rose", label: "Corações", kind: "padrao", colors: ["#be123c", "#4c0519"], pattern: "coracoes" },
  { id: "padrao-coracoes-roxo", label: "Corações roxos", kind: "padrao", colors: ["#6d28d9", "#2e1065"], pattern: "coracoes" }
];

function drawBackgroundLayer(ctx, size, style) {
  fillBackground(ctx, size, style.colors);
  const patternFn = style.pattern && BACKGROUND_PATTERNS[style.pattern];
  if (patternFn) {
    patternFn(ctx, size);
  } else {
    drawSoftGlowCircles(ctx, size);
  }
}

function getStyleById(id) {
  return BACKGROUND_STYLES.find((s) => s.id === id) || BACKGROUND_STYLES[0];
}

// Opcoes de "Tamanho do texto" (nome/frase/selo) -- pedido: dar pra
// aumentar a letra pra quem ve a arte pequena. scale multiplica todos os
// tamanhos de fonte e espacamentos do bloco central de uma vez (ver
// drawBirthdayArt).
const TEXT_SCALE_OPTIONS = [
  { id: "padrao", label: "Padrão", scale: 1 },
  { id: "grande", label: "Grande", scale: 1.15 },
  { id: "extra-grande", label: "Extra grande", scale: 1.3 }
];

function getTextScaleById(id) {
  return TEXT_SCALE_OPTIONS.find((s) => s.id === id) || TEXT_SCALE_OPTIONS[0];
}

// Opcoes de fonte -- todas ja carregadas pelo app (ver o <link> do Google
// Fonts em public/index.html), entao trocar aqui nao depende de baixar fonte
// nova: "Moderno" e a DM Sans padrao do app inteiro; "Elegante" e a
// Cormorant Garamond ja usada na tela de splash; "Classica", "Arredondada"
// e "Manuscrita" foram acrescentadas pra dar mais variedade de estilo ao
// cartao (Playfair Display, Poppins e Caveat, respectivamente).
const FONT_OPTIONS = [
  { id: "moderno", label: "Moderno", family: "'DM Sans', system-ui, sans-serif" },
  { id: "elegante", label: "Elegante", family: "'Cormorant Garamond', Georgia, serif" },
  { id: "classica", label: "Clássica", family: "'Playfair Display', Georgia, serif" },
  { id: "arredondada", label: "Arredondada", family: "'Poppins', system-ui, sans-serif" },
  { id: "manuscrita", label: "Manuscrita", family: "'Caveat', cursive" }
];

function getFontById(id) {
  return FONT_OPTIONS.find((f) => f.id === id) || FONT_OPTIONS[0];
}

// Posicao do logo do site no card -- pedido: o wordmark nao precisa ficar
// sempre em cima do painel, algumas artes (ou gostos da secretaria) combinam
// mais com ele embaixo. So muda onde drawBirthdayArt desenha a imagem; o
// painel e o bloco central de texto nao se mexem.
const LOGO_POSITION_OPTIONS = [
  { id: "topo", label: "No topo" },
  { id: "embaixo", label: "Embaixo" }
];

function getLogoPositionById(id) {
  return LOGO_POSITION_OPTIONS.find((p) => p.id === id) || LOGO_POSITION_OPTIONS[0];
}

// Nome exibido no cartao -- escolha deliberada igual estilo/fonte/tamanho
// acima (nao mais so automatico). "Resumido" e o comportamento de sempre
// (primeiro nome + ultimo sobrenome, ver shortDisplayName); "Completo" usa
// o nome do cadastro por extenso; "Apelido" deixa a secretaria digitar
// como a pessoa e conhecida (ex.: cadastro "Lucas Santos" mas o cartao sai
// "Lucao"), sem precisar mexer na ficha de cadastro pra isso.
const NAME_MODE_OPTIONS = [
  { id: "resumido", label: "Nome resumido" },
  { id: "completo", label: "Nome completo" },
  { id: "apelido", label: "Apelido" }
];


// Default sensato por tipo de pessoa -- a secretaria pode trocar na galeria,
// isso so evita todo mundo comecar com a mesma cara.
function defaultStyleIdForPerson(person) {
  return person?.type === "child" ? "padrao-confete" : "degrade-sunset";
}

function slugify(value) {
  return String(value || "pessoa")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// No cartao a gente so quer o PRIMEIRO nome + ULTIMO sobrenome (ex: "Maria
// Aparecida da Silva Santos" -> "Maria Santos") -- o nome completo (cadastro)
// continua aparecendo na lista/tag do admin junto com a idade, so pra
// identificacao; no cartao em si o nome por extenso ficava grande demais e
// competia com o resto do layout. Partículas comuns ("de", "da", "do",
// "dos", "das", "e") sao ignoradas na escolha do sobrenome, senao um nome
// como "Ana de Souza" viraria "Ana de" em vez de "Ana Souza".
const NAME_PARTICLES = new Set(["de", "da", "do", "dos", "das", "e"]);
function shortDisplayName(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] || "";
  const first = parts[0];
  let lastIdx = parts.length - 1;
  while (lastIdx > 0 && NAME_PARTICLES.has(parts[lastIdx].toLowerCase())) {
    lastIdx -= 1;
  }
  const last = parts[lastIdx];
  return last === first ? first : `${first} ${last}`;
}

// Quebra `text` em linhas que cabem em `maxWidth` (ctx.font ja precisa estar
// setado com a fonte/tamanho que vai desenhar) -- usado tanto pra MEDIR a
// altura de um bloco de texto (antes de desenhar, pra poder centralizar o
// bloco inteiro dentro do painel) quanto pra desenhar de fato via
// drawTextBlock.
function wrapLines(ctx, text, maxWidth) {
  const words = text.split(" ");
  let line = "";
  const lines = [];
  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// Desenha `lines` (ja quebradas por wrapLines) a partir do TOPO do bloco
// (`topY`), nao da baseline -- `ascentRatio` aproxima onde fica a baseline
// da primeira linha dentro de `lineHeight` (0.78 funciona bem pra fontes
// sans nesses tamanhos). Devolve o Y logo abaixo da ultima linha, pra quem
// chamou poder empilhar o proximo bloco sem sobrepor nem precisar de
// deslocamentos manuais por tamanho de fonte (o jeito antigo, com numeros
// tipo "panelY + 24 + 68 + 46" espalhados pelo codigo).
function drawTextBlock(ctx, lines, x, topY, lineHeight, ascentRatio = 0.78) {
  const baseline = topY + lineHeight * ascentRatio;
  lines.forEach((l, i) => ctx.fillText(l, x, baseline + i * lineHeight));
  return topY + lines.length * lineHeight;
}

const CANVAS_SIZE = 1080;
const FONT_FAMILY = "'DM Sans', system-ui, sans-serif";

// Icone de bolo desenhado em SVG (original, sem depender de fonte de emoji
// nem de nenhuma biblioteca de icones) -- rasterizado numa <img> e desenhado
// no canvas junto com o logo, no lugar do emoji "🎂" que dependia de cada
// sistema operacional ter (ou não) fonte colorida de emoji.
const CAKE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <rect x="46" y="16" width="8" height="22" rx="3" fill="#FDE68A"/>
  <path d="M50 6c-5 4-5 9 0 14c5-5 5-10 0-14z" fill="#F08A3C"/>
  <rect x="28" y="40" width="44" height="22" rx="8" fill="#FFFFFF"/>
  <circle cx="33" cy="40" r="6" fill="#FFFFFF"/>
  <circle cx="45" cy="40" r="6" fill="#FFFFFF"/>
  <circle cx="57" cy="40" r="6" fill="#FFFFFF"/>
  <circle cx="67" cy="40" r="6" fill="#FFFFFF"/>
  <rect x="12" y="62" width="76" height="28" rx="10" fill="#F3A952"/>
  <circle cx="18" cy="62" r="8" fill="#F3A952"/>
  <circle cx="34" cy="62" r="8" fill="#F3A952"/>
  <circle cx="50" cy="62" r="8" fill="#F3A952"/>
  <circle cx="66" cy="62" r="8" fill="#F3A952"/>
  <circle cx="82" cy="62" r="8" fill="#F3A952"/>
  <ellipse cx="50" cy="92" rx="40" ry="6" fill="rgba(0,0,0,0.18)"/>
</svg>`;

function svgToDataUrl(svg) {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    // Sem logo/icone a arte ainda funciona, só com texto -- por isso resolve
    // com null em vez de rejeitar (rede lenta ou arquivo indisponível não
    // pode quebrar a geração da arte).
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Carrega 1x só (promise cacheada no módulo) e é reaproveitado por todos os
// cards de aniversariante da página -- evita relançar a mesma imagem/SVG a
// cada card.
let artAssetsPromise = null;
function loadArtAssets() {
  if (!artAssetsPromise) {
    artAssetsPromise = Promise.all([
      loadImage("/institucional/logo-mosaico-wordmark.png"),
      loadImage(svgToDataUrl(CAKE_ICON_SVG))
    ]).then(([logo, cakeIcon]) => ({ logo, cakeIcon }));
  }
  return artAssetsPromise;
}

// photoImg: um HTMLImageElement ja carregado a partir de um arquivo local
// (ver handlePhotoChange em BirthdayArtCard) -- nunca sobe pro servidor, so
// existe na memoria do navegador enquanto o card estiver aberto. Quando
// presente, substitui o icone de bolo por um retrato circular no topo do
// cartao; sem foto, o comportamento (icone de bolo) continua igual.
function drawBirthdayArt(canvas, { displayName, message, day, month }, assets, style, textScale = 1, fontFamily = FONT_FAMILY, logoPosition = "topo", photoImg = null) {
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  drawBackgroundLayer(ctx, CANVAS_SIZE, style || BACKGROUND_STYLES[0]);

  const panelX = 80;
  const panelY = 230;
  const panelW = CANVAS_SIZE - panelX * 2;

  ctx.textAlign = "center";
  const centerX = CANVAS_SIZE / 2;

  // Bloco central (icone + "Feliz Aniversário!" + nome + frase) -- mede a
  // altura do bloco com o mesmo wrap que vai usar pra desenhar ANTES de
  // decidir a altura do painel (ver mais abaixo). textScale (escolhido na
  // galeria "Tamanho do texto") multiplica todos os tamanhos/espacamentos
  // do bloco de uma vez. Com foto: pedido foi "mais destaque, mover de
  // lugar" -- o retrato e bem maior que o icone de bolo (224 vs 60px base)
  // E fica parcialmente por cima da borda do painel (efeito "crachá"/selo),
  // em vez de encolhido todo para dentro do painel como o icone de bolo.
  const iconSize = (photoImg ? 224 : 60) * textScale;
  const iconGap = (photoImg ? 34 : 22) * textScale;
  // "Feliz Aniversário!" virou um selo de apoio (era 54px, mesmo peso do
  // nome -- ninguem sabia se olhava pro nome ou pro texto generico
  // primeiro). O nome e o heroi de um cartao de aniversario personalizado;
  // "Feliz Aniversário!" e so o contexto.
  const eyebrowSize = 30 * textScale;
  const eyebrowLH = 38 * textScale;
  const eyebrowGap = 18 * textScale;
  // Nome promovido a heroi do card: maior, mais peso, branco puro (maximo
  // contraste) -- antes vinha em dourado no mesmo tamanho do texto generico.
  const nameGap = 30 * textScale;
  const messageSize = 30 * textScale;
  const messageLH = 40 * textScale;

  ctx.font = `700 ${eyebrowSize}px ${fontFamily}`;
  const eyebrowLines = wrapLines(ctx, "Feliz Aniversário!", panelW - 160);

  // Nome abreviado (primeiro + ultimo sobrenome, ver shortDisplayName) no
  // tamanho padrao (62px) cabe numa linha na quase totalidade dos casos.
  // Quando mesmo abreviado ainda nao cabe (nomes compostos grandes tipo
  // "Maria Aparecida Santos"), reduz o tamanho em passos ate caber em 1
  // linha, em vez de deixar quebrar em 2+ linhas gigantes.
  // O chamador (BirthdayArtCard) ja decide o texto final (resumido/completo/
  // apelido, ver NAME_MODE_OPTIONS) -- aqui so desenha o que recebeu, sem
  // re-abreviar de novo.
  const shortName = displayName;
  const nameSizeBase = 62 * textScale;
  const nameSizeSteps = [nameSizeBase, nameSizeBase * 0.8, nameSizeBase * 0.65];
  let nameSize = nameSizeSteps[0];
  let nameLines = [];
  for (const size of nameSizeSteps) {
    ctx.font = `800 ${size}px ${fontFamily}`;
    const lines = wrapLines(ctx, shortName, panelW - 100);
    nameSize = size;
    nameLines = lines;
    if (lines.length <= 1) break;
  }
  const nameLH = nameSize * (68 / 62);

  ctx.font = `500 ${messageSize}px ${fontFamily}`;
  const messageLines = wrapLines(ctx, message, panelW - 160);

  const dateReserve = 76; // espaco fixo reservado pra data, embaixo do painel
  const peek = 46 * textScale; // (só com foto) quanto do retrato aparece ACIMA da borda do painel
  const textContentHeight =
    eyebrowLines.length * eyebrowLH + eyebrowGap +
    nameLines.length * nameLH + nameGap +
    messageLines.length * messageLH;

  // Altura do painel: ANTES era fixa (680px) e o texto podia estourar pra
  // fora dela em combinações extremas (foto grande + "Extra grande" + nome/
  // frase compridos -- bug reportado: "estoura tbm o texto dependendo da
  // combinacao", texto sobrepondo a data). Agora o painel mede quanto de
  // espaço o ícone/foto + o texto realmente precisam e cresce pra caber,
  // com um teto que nunca estoura o canvas (deixa uma margem embaixo).
  const topReserve = photoImg ? (iconSize - peek) + iconGap : 48 + iconSize + iconGap;
  const bottomBreathing = 40;
  const neededPanelH = topReserve + textContentHeight + bottomBreathing + dateReserve;
  const minPanelH = 680; // igual ao valor fixo antigo -- cartões com texto curto continuam com essa cara
  const maxPanelH = CANVAS_SIZE - panelY - 40; // sempre sobra pelo menos 40px de fundo visível abaixo do painel
  const panelH = Math.min(maxPanelH, Math.max(minPanelH, neededPanelH));

  // Painel quase opaco (era 0.55 -- translucido o bastante pra misturar com
  // o fundo e sujar a cor num marrom/oliva sem graca, principalmente com
  // paletas/padroes mais saturados). Com 0.85 o painel fica com a cor dele
  // mesmo, estavel, nao importa qual dos 18 estilos de fundo esteja
  // escolhido na galeria -- e o que garante o contraste do texto por dentro
  // do painel em qualquer combinacao.
  ctx.fillStyle = "rgba(8,13,24,0.85)";
  if (typeof ctx.roundRect === "function") {
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, panelW, panelH, 32);
    ctx.fill();
  } else {
    ctx.fillRect(panelX, panelY, panelW, panelH);
  }

  // Logo do site (o wordmark "Igreja Batista Mosaico" do cabecalho do site
  // institucional, ver InstitutionalLayout.jsx) acima do painel -- no lugar
  // do simbolo isolado de antes, pra arte carregar a marca completa e
  // reconhecivel de cara. Como o wordmark ja escreve o nome da igreja, a
  // pilula de texto "IGREJA BATISTA MOSAICO" que existia embaixo dele foi
  // removida (ficava repetindo a mesma informacao 2x).
  if (assets?.logo) {
    const logoH = 92;
    const logoW = logoH * (assets.logo.naturalWidth / assets.logo.naturalHeight);
    // "topo": mesmo lugar de sempre, no vao acima do painel. "embaixo": vao
    // livre entre o fim do painel e a borda do canvas (nada mais e desenhado
    // ali -- a data fica dentro do painel, ver mais abaixo), centralizado
    // verticalmente nesse espaco.
    const logoY = logoPosition === "embaixo"
      ? panelY + panelH + (CANVAS_SIZE - (panelY + panelH) - logoH) / 2
      : 54;
    ctx.drawImage(assets.logo, centerX - logoW / 2, logoY, logoW, logoH);
  }

  if (photoImg) {
    // Retrato em destaque: posição FIXA (não entra na centralização do bloco
    // de texto abaixo), com boa parte dele "escapando" por cima da borda do
    // painel -- pedido: dar mais destaque e mudar o retrato de lugar em vez
    // de deixá-lo encolhido no mesmo canto que era do ícone de bolo.
    const r = iconSize / 2;
    const circleCenterY = panelY - peek + r;
    const photoBottom = circleCenterY + r;
    const textTop = photoBottom + iconGap;
    const availableForText = panelY + panelH - dateReserve - textTop;
    let blockY = textContentHeight <= availableForText
      ? textTop + (availableForText - textContentHeight) / 2
      : textTop; // nome/frase longos demais pra centralizar -- ancora logo abaixo do retrato

    // Sombra suave atrás do retrato (fill quase invisível só pra gerar
    // shadow), pra ele parecer de fato "por cima" do fundo/painel, não colado.
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = 28 * textScale;
    ctx.beginPath();
    ctx.arc(centerX, circleCenterY, r, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.01)";
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, circleCenterY, r, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    // "cover" -- corta o excesso da foto em vez de espremer/distorcer o rosto.
    const scale = Math.max(iconSize / photoImg.naturalWidth, iconSize / photoImg.naturalHeight);
    const drawW = photoImg.naturalWidth * scale;
    const drawH = photoImg.naturalHeight * scale;
    ctx.drawImage(photoImg, centerX - drawW / 2, circleCenterY - drawH / 2, drawW, drawH);
    ctx.restore();
    ctx.beginPath();
    ctx.arc(centerX, circleCenterY, r, 0, Math.PI * 2);
    ctx.lineWidth = 7;
    ctx.strokeStyle = "rgba(255,255,255,0.92)";
    ctx.stroke();

    ctx.font = `700 ${eyebrowSize}px ${fontFamily}`;
    ctx.fillStyle = "rgba(253,230,138,0.85)";
    blockY = drawTextBlock(ctx, eyebrowLines, centerX, blockY, eyebrowLH) + eyebrowGap;

    ctx.font = `800 ${nameSize}px ${fontFamily}`;
    ctx.fillStyle = "#FFFFFF";
    blockY = drawTextBlock(ctx, nameLines, centerX, blockY, nameLH) + nameGap;

    ctx.font = `500 ${messageSize}px ${fontFamily}`;
    ctx.fillStyle = "rgba(235,241,255,0.82)";
    drawTextBlock(ctx, messageLines, centerX, blockY, messageLH);
  } else {
    const contentHeight = iconSize + iconGap + textContentHeight;

    const availableForCentering = panelH - dateReserve - 96;
    let blockY = contentHeight <= availableForCentering
      ? panelY + (panelH - dateReserve - contentHeight) / 2
      : panelY + 48; // nome/frase longos demais pra centralizar -- ancora no topo

    if (assets?.cakeIcon) {
      ctx.drawImage(assets.cakeIcon, centerX - iconSize / 2, blockY, iconSize, iconSize);
    }
    blockY += iconSize + iconGap;

    ctx.font = `700 ${eyebrowSize}px ${fontFamily}`;
    ctx.fillStyle = "rgba(253,230,138,0.85)";
    blockY = drawTextBlock(ctx, eyebrowLines, centerX, blockY, eyebrowLH) + eyebrowGap;

    ctx.font = `800 ${nameSize}px ${fontFamily}`;
    ctx.fillStyle = "#FFFFFF";
    blockY = drawTextBlock(ctx, nameLines, centerX, blockY, nameLH) + nameGap;

    ctx.font = `500 ${messageSize}px ${fontFamily}`;
    ctx.fillStyle = "rgba(235,241,255,0.82)";
    drawTextBlock(ctx, messageLines, centerX, blockY, messageLH);
  }

  if (day && month) {
    ctx.font = `700 26px ${fontFamily}`;
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.fillText(`${day} de ${MONTH_NAMES[month - 1]}`, centerX, panelY + panelH - 34);
  }
}

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const badgeStyle = (type) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 9px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  border: type === "member" ? "1px solid rgba(125,211,252,0.4)" : "1px solid rgba(139,124,255,0.4)",
  background: type === "member" ? "rgba(14,165,233,0.14)" : "rgba(139,124,255,0.14)",
  color: type === "member" ? "#7DD3FC" : "#c4b8ff"
});

const buttonPrimaryStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "9px 14px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.24)",
  background: UI.brand,
  color: "#1a140a",
  fontWeight: 800,
  fontSize: 13,
  cursor: "pointer"
};

const buttonGhostStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "9px 14px",
  borderRadius: 10,
  border: "1px solid rgba(148,163,184,0.35)",
  background: "rgba(148,163,184,0.08)",
  color: "#e5ecff",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer"
};

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

const THUMB_SIZE = 64;

// Miniatura clicável de 1 estilo de fundo -- desenha o mesmo
// drawBackgroundLayer usado na arte principal, só que num canvas pequeno
// (THUMB_SIZE), pra secretaria ver de verdade a cor/padrão antes de
// escolher, em vez de adivinhar por um nome.
function StyleThumb({ style, selected, onClick }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawBackgroundLayer(canvas.getContext("2d"), THUMB_SIZE, style);
  }, [style]);

  return (
    <button
      type="button"
      onClick={onClick}
      title={style.label}
      aria-pressed={selected}
      style={{
        padding: 3,
        borderRadius: 12,
        border: selected ? "2px solid #FDE68A" : "2px solid rgba(255,255,255,0.14)",
        background: "transparent",
        cursor: "pointer",
        lineHeight: 0
      }}
    >
      <canvas
        ref={canvasRef}
        width={THUMB_SIZE}
        height={THUMB_SIZE}
        style={{ width: 38, height: 38, borderRadius: 9, display: "block" }}
      />
    </button>
  );
}

const STYLE_GROUPS = [
  { kind: "liso", label: "Liso" },
  { kind: "degrade", label: "Degradê" },
  { kind: "padrao", label: "Padrão" }
];

// Galeria de estilos de fundo, agrupada por tipo (liso / degradê / padrão) --
// é o que dá pra secretaria escolher de propósito algo mais lúdico pra uma
// criança ou mais sóbrio pra um membro adulto, em vez da arte sortear
// sozinha (ver defaultStyleIdForPerson pro ponto de partida de cada um).
function NameModeGallery({ modeId, onSelect, apelidoText, onApelidoChange }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ margin: "0 0 6px", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: "#7c8db0", fontWeight: 800 }}>
        Nome no cartão
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: modeId === "apelido" ? 8 : 0 }}>
        {NAME_MODE_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onSelect(option.id)}
            aria-pressed={option.id === modeId}
            style={textOptionButtonStyle(option.id === modeId)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {modeId === "apelido" && (
        <input
          type="text"
          value={apelidoText}
          onChange={(e) => onApelidoChange(e.target.value)}
          placeholder="Como a pessoa é conhecida (ex.: Lucão)"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.15)",
            background: "rgba(255,255,255,0.05)",
            color: "#FFF",
            fontSize: 14
          }}
        />
      )}
    </div>
  );
}

function StyleGallery({ styleId, onSelect }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ margin: "0 0 8px", fontSize: 11, color: "#9fb0cc", fontWeight: 700 }}>
        Estilo do fundo
      </p>
      {STYLE_GROUPS.map((group) => {
        const items = BACKGROUND_STYLES.filter((s) => s.kind === group.kind);
        if (items.length === 0) return null;
        return (
          <div key={group.kind} style={{ marginBottom: 8 }}>
            <p style={{ margin: "0 0 6px", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: "#7c8db0", fontWeight: 800 }}>
              {group.label}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {items.map((style) => (
                <StyleThumb
                  key={style.id}
                  style={style}
                  selected={style.id === styleId}
                  onClick={() => onSelect(style.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function textOptionButtonStyle(selected) {
  return {
    padding: "7px 12px",
    borderRadius: 999,
    border: selected ? "1.5px solid #FDE68A" : "1px solid rgba(255,255,255,0.16)",
    background: selected ? "rgba(253,230,138,0.14)" : "rgba(255,255,255,0.03)",
    color: selected ? "#FDE68A" : "#CBD5E1",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer"
  };
}

// Tamanho do texto e fonte -- mais simples que StyleGallery (nao precisa de
// canvas de preview, sao so numeros/familias de fonte), mas segue o mesmo
// padrao de escolha deliberada da secretaria por baixo do card.
function TextOptionsGallery({ scaleId, onScaleSelect, fontId, onFontSelect, logoPositionId, onLogoPositionSelect }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ margin: "0 0 6px", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: "#7c8db0", fontWeight: 800 }}>
        Tamanho do texto
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {TEXT_SCALE_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onScaleSelect(option.id)}
            aria-pressed={option.id === scaleId}
            style={textOptionButtonStyle(option.id === scaleId)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <p style={{ margin: "0 0 6px", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: "#7c8db0", fontWeight: 800 }}>
        Fonte
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {FONT_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onFontSelect(option.id)}
            aria-pressed={option.id === fontId}
            style={{ ...textOptionButtonStyle(option.id === fontId), fontFamily: option.family }}
          >
            {option.label}
          </button>
        ))}
      </div>
      <p style={{ margin: "0 0 6px", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: "#7c8db0", fontWeight: 800 }}>
        Posição do logo
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {LOGO_POSITION_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onLogoPositionSelect(option.id)}
            aria-pressed={option.id === logoPositionId}
            style={textOptionButtonStyle(option.id === logoPositionId)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Upload de foto -- so pra gerar a arte na hora, nunca sobe pro servidor
// (ver comentario em drawBirthdayArt e handlePhotoChange abaixo). Sem foto
// escolhida, o cartao continua com o icone de bolo de sempre.
function PhotoUpload({ hasPhoto, onChange, onRemove }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ margin: "0 0 8px", fontSize: 11, color: "#9fb0cc", fontWeight: 700 }}>
        Foto (opcional)
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <label style={{ ...buttonGhostStyle, margin: 0 }}>
          <ImagePlus size={14} /> {hasPhoto ? "Trocar foto" : "Enviar foto"}
          <input type="file" accept="image/*" onChange={onChange} style={{ display: "none" }} />
        </label>
        {hasPhoto && (
          <button type="button" style={buttonGhostStyle} onClick={onRemove}>
            <X size={14} /> Remover foto
          </button>
        )}
      </div>
      <p style={{ margin: "6px 0 0", color: "#7c8db0", fontSize: 11, lineHeight: 1.5 }}>
        A foto fica só nesta tela, pra gerar o cartão -- não é enviada nem guardada em lugar nenhum.
      </p>
    </div>
  );
}

function BirthdayArtCard({ person, today }) {
  const canvasRef = useRef(null);
  const [messageState, setMessageState] = useState(() => pickRandomMessage());
  // Estilo de fundo escolhido DELIBERADAMENTE na galeria (ver StyleGallery),
  // não mais sorteado -- a secretaria pode combinar o estilo com quem está
  // fazendo aniversário (algo lúdico pra uma criança, mais sóbrio pra um
  // membro adulto) em vez de toda arte sair com a mesma cara fria. Começa
  // num estilo padrão sensato por tipo de pessoa e a secretaria troca
  // quando quiser.
  const [styleId, setStyleId] = useState(() => defaultStyleIdForPerson(person));
  // Nome exibido no cartao -- ver NAME_MODE_OPTIONS. Comeca em "resumido"
  // (mesmo comportamento de sempre); "apelido" comeca vazio e cai de volta
  // pro resumido ate a secretaria digitar algo (ver displayName abaixo).
  const [nameModeId, setNameModeId] = useState("resumido");
  const [apelidoText, setApelidoText] = useState("");
  // "Tamanho do texto" e "Fonte" -- mesma logica de escolha deliberada do
  // styleId acima, cada card guarda a propria escolha.
  const [textScaleId, setTextScaleId] = useState("padrao");
  const [fontId, setFontId] = useState("moderno");
  const [logoPositionId, setLogoPositionId] = useState("topo");
  // Foto opcional enviada pela secretaria -- fica só na memória deste card
  // (um HTMLImageElement, nunca um upload de verdade pro backend). Some se a
  // página recarregar, e é exatamente esse o comportamento pedido.
  const [photoImg, setPhotoImg] = useState(null);
  const [copied, setCopied] = useState(false);
  // Logo + icone de bolo (ver loadArtAssets) -- carregam 1x por página
  // (promise cacheada no módulo) e disparam um redesenho quando prontos;
  // até lá, o card já mostra o texto normalmente, só sem as imagens.
  const [assets, setAssets] = useState(null);

  useEffect(() => {
    let alive = true;
    loadArtAssets().then((loaded) => {
      if (alive) setAssets(loaded);
    });
    return () => {
      alive = false;
    };
  }, []);

  const displayName = nameModeId === "completo"
    ? person.fullName
    : nameModeId === "apelido"
      ? (apelidoText.trim() || shortDisplayName(person.fullName))
      : shortDisplayName(person.fullName);

  const redraw = useCallback((message, id, scaleId, fId, logoPosId, photo) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawBirthdayArt(canvas, {
      displayName,
      message,
      day: today.day,
      month: today.month
    }, assets, getStyleById(id), getTextScaleById(scaleId).scale, getFontById(fId).family, getLogoPositionById(logoPosId).id, photo);
  }, [displayName, today.day, today.month, assets]);

  // Sempre lê a frase e o estilo mais recentes por ref -- o efeito de
  // "esperar as fontes" (logo abaixo) só precisa rodar 1x por card (fontes
  // carregam 1x só), não toda vez que a frase muda ou a secretaria troca de
  // estilo na galeria; o redesenho por troca de frase/estilo já é coberto
  // pelo segundo efeito, que depende de messageState/styleId.
  const messageRef = useRef(messageState.text);
  messageRef.current = messageState.text;
  const styleRef = useRef(styleId);
  styleRef.current = styleId;
  const textScaleRef = useRef(textScaleId);
  textScaleRef.current = textScaleId;
  const fontRef = useRef(fontId);
  fontRef.current = fontId;
  const logoPositionRef = useRef(logoPositionId);
  logoPositionRef.current = logoPositionId;
  const photoRef = useRef(photoImg);
  photoRef.current = photoImg;

  useEffect(() => {
    // Espera as fontes carregarem antes do primeiro desenho -- sem isso, o
    // canvas pode desenhar com a fonte padrão do sistema no instante em que
    // a página abre (a fonte web ainda não "pegou"), mesmo ela já estando
    // referenciada no <link> do index.html.
    let alive = true;
    const draw = () => {
      if (alive) redraw(messageRef.current, styleRef.current, textScaleRef.current, fontRef.current, logoPositionRef.current, photoRef.current);
    };
    if (document.fonts?.ready) {
      document.fonts.ready.then(draw).catch(draw);
    } else {
      draw();
    }
    return () => {
      alive = false;
    };
  }, [redraw]);

  useEffect(() => {
    redraw(messageState.text, styleId, textScaleId, fontId, logoPositionId, photoImg);
  }, [messageState, styleId, textScaleId, fontId, logoPositionId, photoImg, displayName, redraw]);

  const handleNewMessage = () => {
    setMessageState((prev) => pickRandomMessage(prev.index));
  };

  // So le o arquivo local (FileReader) e monta uma <img> em memória -- nunca
  // faz upload/POST pra lugar nenhum. Reseta o <input> depois de ler pra dar
  // pra escolher o MESMO arquivo de novo caso a pessoa remova e reenvie.
  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => setPhotoImg(img);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => setPhotoImg(null);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `aniversario-${slugify(displayName)}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const handleCopyText = async () => {
    const text = `${displayName}, ${messageState.text}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      /* clipboard indisponível (ex.: contexto não-seguro) -- sem feedback visual */
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
        <div>
          <p style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 900, color: "#FFF" }}>{person.fullName}</p>
          <span style={badgeStyle(person.type)}>
            {person.type === "member" ? <Users size={11} /> : <Baby size={11} />}
            {person.type === "member" ? "Membro" : "Kids/Teens"}
            {typeof person.age === "number" ? ` · ${person.age} anos` : ""}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
        {/* Preview fica "grudado" (sticky) enquanto os controles ao lado
            rolam -- pedido: dava pra ver a mudança de estilo/fonte sem ter
            que rolar a tela pra cima toda vez pra conferir o resultado.
            Antes o preview tinha largura FIXA (260px, flex-grow 0) e os
            controles cresciam pra ocupar todo o resto -- em tela larga isso
            virava algo tipo 30/70 (preview pequeno, controles enormes).
            Agora as duas colunas dividem o espaço igualmente (mesmo
            flex-basis e mesmo flex-grow), ficando ~50/50. */}
        <div style={{ flex: "1 1 420px", minWidth: 280, position: "sticky", top: 12 }}>
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            style={{ width: "100%", borderRadius: 14, display: "block", border: "1px solid rgba(255,255,255,0.1)" }}
          />
        </div>

        <div style={{ flex: "1 1 420px", minWidth: 280 }}>
          <NameModeGallery
            modeId={nameModeId}
            onSelect={setNameModeId}
            apelidoText={apelidoText}
            onApelidoChange={setApelidoText}
          />

          <StyleGallery styleId={styleId} onSelect={setStyleId} />

          <PhotoUpload hasPhoto={Boolean(photoImg)} onChange={handlePhotoChange} onRemove={handleRemovePhoto} />

          <TextOptionsGallery scaleId={textScaleId} onScaleSelect={setTextScaleId} fontId={fontId} onFontSelect={setFontId} logoPositionId={logoPositionId} onLogoPositionSelect={setLogoPositionId} />

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <button type="button" style={buttonPrimaryStyle} onClick={handleDownload}>
              <Download size={14} /> Baixar imagem
            </button>
            <button type="button" style={buttonGhostStyle} onClick={handleNewMessage}>
              <RefreshCw size={14} /> Outra frase
            </button>
            <button type="button" style={buttonGhostStyle} onClick={handleCopyText}>
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copiado!" : "Copiar texto"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminAniversariantes() {
  const [status, setStatus] = useState("checking");
  const [loading, setLoading] = useState(false);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [error, setError] = useState("");
  const [today] = useState(() => {
    const now = new Date();
    // America/Sao_Paulo -- mesmo cuidado do backend (lib/birthdayScheduler.js),
    // pra "hoje" não virar o dia errado perto da meia-noite conforme o fuso
    // de quem está com o navegador aberto.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return { month: Number(get("month")), day: Number(get("day")) };
  });

  // Simulação de outro dia: os <select> já começam no dia/mês real de hoje
  // (em vez de vazios) -- assim a secretaria só precisa avançar ou voltar um
  // novo dia a partir de onde já está, sem escolher os dois do zero toda
  // vez. "isSimulating" so fica true quando o valor escolhido REALMENTE
  // difere de hoje -- com os selects ja pre-preenchidos com hoje, sem essa
  // checagem a tela entraria em "modo simulação" sozinha assim que abrisse,
  // mesmo mostrando o mesmo dia de sempre. Sem ano -- aniversário não
  // precisa disso, e obrigar a escolher ano só atrapalhava.
  const [simulatedDay, setSimulatedDay] = useState(() => today.day);
  const [simulatedMonth, setSimulatedMonth] = useState(() => today.month);
  const isSimulating =
    Boolean(simulatedDay && simulatedMonth) &&
    (Number(simulatedDay) !== today.day || Number(simulatedMonth) !== today.month);
  const activeDate = isSimulating ? { day: Number(simulatedDay), month: Number(simulatedMonth) } : today;

  // Aba "Mes" -- lista todo mundo que faz aniversario no mes inteiro, pra
  // secretaria conferir quem vem por ai sem precisar simular dia por dia
  // (pedido do Drigo em 17-18/10/2026). Mes independente do simulador de
  // dia acima -- comeca no mes real de hoje.
  const [viewMode, setViewMode] = useState("dia");
  const [monthViewMonth, setMonthViewMonth] = useState(() => today.month);
  const [monthAniversariantes, setMonthAniversariantes] = useState([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState("");
  // Filtro por categoria na aba "Mes" (pedido do Drigo, 29/10/2026) -- so'
  // no front, a categoria (person.type) ja' vem calculada do backend em
  // cada item, entao nao precisa de parametro novo na API. "todos" (padrao),
  // "member" (Membro) ou "child" (Kids/Teens), mesma distincao do badge.
  const [monthCategoryFilter, setMonthCategoryFilter] = useState("todos");

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const effectiveMeRoles = getEffectiveRoles(me);
        // SECRETARIA_CONSULTA tambem gera a arte/texto de aniversariante --
        // essa tela so usa nome, tipo (membro/kids) e data de aniversario,
        // nada que o papel de consulta ja nao veja em Membros/Kids (consulta).
        const canAccess =
          effectiveMeRoles.includes("ADMIN") ||
          effectiveMeRoles.includes("SECRETARIA") ||
          effectiveMeRoles.includes("SECRETARIA_CONSULTA");
        if (canAccess && !me.passwordMustChange) {
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (status !== "ready") return;
    let alive = true;
    setLoading(true);
    const query = isSimulating ? `?month=${activeDate.month}&day=${activeDate.day}` : "";
    apiFetch(`/admin/aniversariantes${query}`, { method: "GET" })
      .then((data) => {
        if (!alive) return;
        setAniversariantes(Array.isArray(data?.aniversariantes) ? data.aniversariantes : []);
        setError("");
      })
      .catch(() => {
        if (alive) setError("Não foi possível carregar os aniversariantes desse dia.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [status, isSimulating, activeDate.month, activeDate.day]);

  useEffect(() => {
    if (status !== "ready" || viewMode !== "mes") return;
    let alive = true;
    setMonthLoading(true);
    apiFetch(`/admin/aniversariantes?month=${monthViewMonth}&scope=month`, { method: "GET" })
      .then((data) => {
        if (!alive) return;
        setMonthAniversariantes(Array.isArray(data?.aniversariantes) ? data.aniversariantes : []);
        setMonthError("");
      })
      .catch(() => {
        if (alive) setMonthError("Não foi possível carregar os aniversariantes do mês.");
      })
      .finally(() => {
        if (alive) setMonthLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [status, viewMode, monthViewMonth]);

  const filteredMonthAniversariantes =
    monthCategoryFilter === "todos"
      ? monthAniversariantes
      : monthAniversariantes.filter((person) => person.type === monthCategoryFilter);

  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita à equipe administrativa. Entre com uma conta autorizada para ver o painel.</CenteredMessage>;
  }

  const todayLabel = `${activeDate.day} de ${MONTH_NAMES[activeDate.month - 1]}`;

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>
          Área administrativa
        </p>
        <h1 style={{ margin: "0 0 6px", fontSize: 26, fontWeight: 950, color: "#FFF", display: "flex", alignItems: "center", gap: 10 }}>
          <Cake size={24} /> {isSimulating ? "Aniversariantes do dia simulado" : "Aniversariantes de hoje"}
        </h1>
        <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6, maxWidth: 620 }}>
          {isSimulating ? `Simulando ${todayLabel}` : todayLabel} -- gere a arte de parabéns ou copie o texto pra mandar no WhatsApp de cada aniversariante.
        </p>

        {/* Aba "Dia" (padrão, com simulador + arte) vs "Mês" (lista corrida
            de todo mundo que faz aniversário no mês, pra secretaria se
            planejar com antecedência). Mesmo visual de abas do AdminMembros.jsx. */}
        <div role="tablist" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          {[
            { key: "dia", label: "Dia", icon: CalendarClock },
            { key: "mes", label: "Mês", icon: CalendarRange }
          ].map((item) => {
            const selected = viewMode === item.key;
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setViewMode(item.key)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  minHeight: 40,
                  padding: "0 16px",
                  marginBottom: -1,
                  border: "none",
                  borderBottom: selected ? "2px solid #7DD3FC" : "2px solid transparent",
                  background: "transparent",
                  color: selected ? "#FFF" : "#9fb0cc",
                  fontSize: 13.5,
                  fontWeight: selected ? 900 : 700,
                  cursor: "pointer"
                }}
              >
                <Icon size={14} /> {item.label}
              </button>
            );
          })}
        </div>

        {viewMode === "mes" && (
          <div>
            <div style={{ ...cardStyle, padding: 22, marginBottom: 20 }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 220 }}>
                  <CalendarRange size={26} color="#7DD3FC" />
                  <div>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 900, color: "#FFF" }}>Aniversariantes do mês</p>
                    <p style={{ margin: "2px 0 0", fontSize: 12, color: "#7c8db0" }}>Todo mundo que faz aniversário nesse mês, em ordem de dia</p>
                  </div>
                </div>
                <select
                  value={monthViewMonth}
                  onChange={(event) => setMonthViewMonth(Number(event.target.value))}
                  style={{ padding: "14px 14px", borderRadius: 12, border: "1px solid rgba(148,163,184,0.35)", background: "rgba(148,163,184,0.08)", color: "#e5ecff", fontWeight: 800, fontSize: 16, fontFamily: "inherit", minWidth: 180 }}
                >
                  {MONTH_NAMES.map((name, index) => (
                    <option key={name} value={index + 1}>{name.charAt(0).toUpperCase() + name.slice(1)}</option>
                  ))}
                </select>
                <div role="group" aria-label="Filtrar por categoria" style={{ display: "inline-flex", gap: 6, padding: 4, borderRadius: 12, background: "rgba(148,163,184,0.08)", border: "1px solid rgba(148,163,184,0.2)" }}>
                  {[
                    { key: "todos", label: "Todos" },
                    { key: "member", label: "Membros" },
                    { key: "child", label: "Kids e Teens" }
                  ].map((option) => {
                    const selected = monthCategoryFilter === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setMonthCategoryFilter(option.key)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: 9,
                          border: "none",
                          background: selected ? "rgba(125,211,252,0.16)" : "transparent",
                          color: selected ? "#7DD3FC" : "#9fb0cc",
                          fontSize: 12.5,
                          fontWeight: selected ? 900 : 700,
                          cursor: "pointer",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {monthLoading && <p style={{ color: "#9fb0cc", fontSize: 14 }}>Carregando...</p>}
            {!monthLoading && monthError && <p style={{ color: "#FCA5A5", fontSize: 14 }}>{monthError}</p>}
            {!monthLoading && !monthError && monthAniversariantes.length === 0 && (
              <p style={{ color: "#9fb0cc", fontSize: 14 }}>Ninguém faz aniversário em {MONTH_NAMES[monthViewMonth - 1]}.</p>
            )}
            {!monthLoading && !monthError && monthAniversariantes.length > 0 && filteredMonthAniversariantes.length === 0 && (
              <p style={{ color: "#9fb0cc", fontSize: 14 }}>Ninguém dessa categoria faz aniversário em {MONTH_NAMES[monthViewMonth - 1]}.</p>
            )}
            {!monthLoading && !monthError && filteredMonthAniversariantes.length > 0 && (
              <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
                {filteredMonthAniversariantes.map((person, index) => (
                  <div
                    key={`${person.type}-${person.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: "14px 20px",
                      borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.07)"
                    }}
                  >
                    <div style={{ minWidth: 44, textAlign: "center" }}>
                      <p style={{ margin: 0, fontSize: 18, fontWeight: 900, color: "#FFF" }}>{String(person.day).padStart(2, "0")}</p>
                      <p style={{ margin: 0, fontSize: 10, color: "#7c8db0", textTransform: "uppercase" }}>{MONTH_NAMES[monthViewMonth - 1].slice(0, 3)}</p>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 14.5, fontWeight: 800, color: "#e5ecff" }}>{person.fullName}</p>
                      {typeof person.age === "number" && (
                        <p style={{ margin: "2px 0 0", fontSize: 12, color: "#7c8db0" }}>Completa {person.age} anos</p>
                      )}
                    </div>
                    <span style={badgeStyle(person.type)}>
                      {person.type === "member" ? <Users size={11} /> : <Baby size={11} />}
                      {person.type === "member" ? "Membro" : "Kids/Teens"}
                    </span>
                    <button
                      type="button"
                      style={{ ...buttonGhostStyle, whiteSpace: "nowrap" }}
                      onClick={() => {
                        setSimulatedMonth(monthViewMonth);
                        setSimulatedDay(person.day);
                        setViewMode("dia");
                        window.scrollTo(0, 0);
                      }}
                    >
                      Gerar arte
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {viewMode === "dia" && (
        <>
        {/* Simulador de data: virou uma barra de largura total, sempre
            expandida (antes vinha encolhido do lado da lista, escondido
            atrás de um botão "Simular outro dia") -- pedido explícito:
            "ta pequeno ai o simulador, seria interessante ver ele mais
            ampliado". Selects maiores e sempre visíveis, sem toggle. */}
        <div style={{ ...cardStyle, padding: 22, marginBottom: 20 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 220 }}>
              <CalendarClock size={26} color="#7DD3FC" />
              <div>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 900, color: "#FFF" }}>Simulador de data</p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#7c8db0" }}>Veja quem faria aniversário em outro dia</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={simulatedDay}
                onChange={(event) => setSimulatedDay(event.target.value)}
                style={{ padding: "14px 14px", borderRadius: 12, border: "1px solid rgba(148,163,184,0.35)", background: "rgba(148,163,184,0.08)", color: "#e5ecff", fontWeight: 800, fontSize: 16, fontFamily: "inherit", width: 92 }}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
              <select
                value={simulatedMonth}
                onChange={(event) => setSimulatedMonth(event.target.value)}
                style={{ padding: "14px 14px", borderRadius: 12, border: "1px solid rgba(148,163,184,0.35)", background: "rgba(148,163,184,0.08)", color: "#e5ecff", fontWeight: 800, fontSize: 16, fontFamily: "inherit", minWidth: 180 }}
              >
                {MONTH_NAMES.map((name, index) => (
                  <option key={name} value={index + 1}>{name.charAt(0).toUpperCase() + name.slice(1)}</option>
                ))}
              </select>
              {isSimulating && (
                <button
                  type="button"
                  style={buttonGhostStyle}
                  onClick={() => {
                    setSimulatedDay(today.day);
                    setSimulatedMonth(today.month);
                  }}
                >
                  Voltar para hoje
                </button>
              )}
            </div>
          </div>
          {isSimulating && (
            <p style={{ margin: "14px 0 0", color: "#FCD34D", fontSize: 13, fontWeight: 700, lineHeight: 1.5 }}>
              Modo simulação: a lista e as artes abaixo mostram o dia escolhido, não o dia real de hoje.
            </p>
          )}
        </div>

        <div>
          {loading && <p style={{ color: "#9fb0cc", fontSize: 14 }}>Carregando...</p>}
          {!loading && error && <p style={{ color: "#FCA5A5", fontSize: 14 }}>{error}</p>}
          {!loading && !error && aniversariantes.length === 0 && (
            <p style={{ color: "#9fb0cc", fontSize: 14 }}>{isSimulating ? "Ninguém faz aniversário nesse dia." : "Ninguém faz aniversário hoje."}</p>
          )}

          {!loading && !error && aniversariantes.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(560px, 100%), 1fr))", gap: 16 }}>
              {aniversariantes.map((person) => (
                <BirthdayArtCard key={`${person.type}-${person.id}`} person={person} today={activeDate} />
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
